import { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface WorldClockValue {
  timezone: string;
}

interface WorldClockCellProps {
  value: WorldClockValue | null;
  onChange: (value: WorldClockValue | null) => void;
  readOnly?: boolean;
}

const TIMEZONES = [
  { value: 'America/Sao_Paulo', label: 'São Paulo (BRT)' },
  { value: 'America/New_York', label: 'Nova York (EST)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PST)' },
  { value: 'Europe/London', label: 'Londres (GMT)' },
  { value: 'Europe/Paris', label: 'Paris (CET)' },
  { value: 'Asia/Tokyo', label: 'Tóquio (JST)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Asia/Shanghai', label: 'Xangai (CST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEDT)' },
];

export function WorldClockCell({ value, onChange, readOnly }: WorldClockCellProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [currentTime, setCurrentTime] = useState('');

  const timezone = value?.timezone || 'America/Sao_Paulo';

  useEffect(() => {
    const updateTime = () => {
      try {
        const now = new Date();
        const formatted = new Intl.DateTimeFormat('pt-BR', {
          timeZone: timezone,
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        }).format(now);
        setCurrentTime(formatted);
      } catch (error) {
        setCurrentTime('--:--');
      }
    };

    updateTime();
    const interval = setInterval(updateTime, 60000); // Atualiza a cada minuto

    return () => clearInterval(interval);
  }, [timezone]);

  const getTimezoneLabel = (tz: string) => {
    return TIMEZONES.find((t) => t.value === tz)?.label || tz;
  };

  if (readOnly && !value) {
    return <span className="text-gray-400 text-sm">-</span>;
  }

  if (isEditing) {
    return (
      <div className="space-y-2 p-2 bg-white border rounded shadow-lg min-w-[250px]">
        <Select
          value={timezone}
          onValueChange={(val) => {
            onChange({ timezone: val });
            setIsEditing(false);
          }}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TIMEZONES.map((tz) => (
              <SelectItem key={tz.value} value={tz.value}>
                {tz.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>
          Cancelar
        </Button>
      </div>
    );
  }

  if (!value) {
    return (
      <div
        className="text-gray-400 text-sm cursor-pointer hover:text-gray-600"
        onClick={() => !readOnly && setIsEditing(true)}
      >
        Adicionar fuso horário
      </div>
    );
  }

  return (
    <div
      className={`flex items-center gap-2 p-1 rounded ${
        readOnly ? '' : 'cursor-pointer hover:bg-gray-50'
      }`}
      onClick={() => !readOnly && setIsEditing(true)}
    >
      <Clock className="w-4 h-4 text-blue-600" />
      <span className="font-mono font-semibold text-blue-600">{currentTime}</span>
      <span className="text-xs text-gray-500">({getTimezoneLabel(timezone)})</span>
    </div>
  );
}
